'use client'

import { Lock } from 'lucide-react'

import { getJourneyCardCta } from '../domain/getJourneyCardCta'

import { CardRewards, CardTile, CardTileImage } from '@/shared/ui/Card'
import { AdminAutoCompleteButton } from '@/shared/ui/AdminAutoCompleteButton'
import { Button } from '@/shared/ui/Button'
import { cn } from '@/shared/lib/utils'

import type { JourneyWithProgress } from '@/features/journeys'

interface JourneyCardProps {
  journey: JourneyWithProgress
  showCover: boolean
  isActive: boolean
  onSelect: () => void
  description?: string
}

/**
 * description has no data source yet (no `description` field on Journey or
 * the API) — kept as an optional prop so a future backend addition can
 * populate it without another component change (see prd.md R6.5/R.T.2).
 */
export function JourneyCard({
  journey,
  showCover,
  isActive,
  onSelect,
  description
}: JourneyCardProps) {
  const blocked = journey._progress.blocked
  const blockedByJourneyName = journey._progress.blockedByJourneyName
  const cta = getJourneyCardCta({
    blocked,
    finished: journey._progress.finished,
    blockRedo: Boolean(journey.block_redo),
    started: journey._progress.finishedPercent > 0
  })

  return (
    <CardTile
      onClick={blocked ? undefined : onSelect}
      className={
        blocked
          ? 'grayscale opacity-70 cursor-not-allowed'
          : `cursor-pointer transition-all hover:scale-[1.02] ${
              isActive ? 'ring-2 ring-primary' : ''
            }`
      }
    >
      {showCover && journey.cover?.file?.url ? (
        <CardTileImage src={journey.cover.file.url} alt="" width={68} height={68} />
      ) : null}

      <div className="flex flex-col gap-2 grow">
        <h3 className="text-base lg:text-lg font-semibold text-primary-dark flex items-center gap-1.5">
          {blocked ? <Lock className="size-4 shrink-0" aria-hidden /> : null}
          {journey.name}
        </h3>

        {description ? (
          <p className="text-sm font-semibold line-clamp-2">{description}</p>
        ) : null}

        {blocked ? (
          <p className="text-sm font-semibold line-clamp-2">
            {blockedByJourneyName
              ? `Conclua a jornada ${blockedByJourneyName} para desbloquear.`
              : 'Conclua a jornada anterior para desbloquear.'}
          </p>
        ) : null}

        <div className="w-full rounded-sm bg-secondary/20 h-4 text-center text-white text-xs font-sans relative overflow-hidden">
          <div
            className="absolute top-0 left-0 h-full bg-primary transition-all"
            style={{ width: `${journey._progress.finishedPercent}%` }}
          />
          <span className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-10">
            {journey._progress.finishedPercent}%
          </span>
        </div>
      </div>

      <div className="flex flex-col items-end gap-2 shrink-0">
        <CardRewards
          layout="inline"
          coinReward={journey._progress.rewards.totalCoins}
          pointReward={journey._progress.rewards.totalPoints}
          acquiredPointReward={journey._progress.rewards.acquiredPoints}
          completed={journey._progress.finished}
          claimed={journey._progress.finished}
          withClaimedBadge
        />
        <Button
          variant={cta.variant}
          size="sm"
          disabled={cta.disabled && !cta.muted}
          aria-disabled={cta.disabled || undefined}
          className={cn(
            // Largura fixa pela maior palavra (Bloqueada/Concluída/Continuar)
            // para todos os estados terem o mesmo tamanho.
            'min-w-28',
            cta.muted && 'opacity-60 pointer-events-none'
          )}
          onClick={cta.disabled ? undefined : onSelect}
        >
          {cta.label}
        </Button>

        {/* O card da lista é o único ponto alcançável quando a jornada está
            travada — a tela interna não abre. É daqui que o suporte conclui a
            cadeia de pré-requisitos para chegar ao conteúdo. */}
        <AdminAutoCompleteButton type="Journey" id={journey.id} />
      </div>
    </CardTile>
  )
}
